import { cn } from '@herit-fe/heritage-ui';
import { PlugZap } from 'lucide-react';
import type { PlantStatus } from '@/shared/data-types';

/**
 * 인버터 표현 — **아이콘 타일 + 상태 점** 한 쌍. 인버터 상세분석의 구성 카드(`widgets/inverter-config-list`)와
 * 발전량 조회조건의 인버터 선택(`features/generation-filter`)이 공유한다(같은 설비를 다른 화면에서 다르게
 * 그리지 않도록 SSOT 승격).
 *
 * 상태를 아이콘에 싣지 않고 **이름 옆 컬러 점**으로 분리하는 게 이 표현의 규칙 — 아이콘은 "인버터"라는 종류만
 * 말하고, 상태는 점이 말한다(구앱은 인버터 사진 위에 상태 스크림을 덮었으나 web 은 이 표현으로 통일).
 */

// 상태 → 점 색(상태 팔레트 `--heviton-*`). `StatusGlyph` 와 동일 시맨틱을 bg 로(작은 점이라 글리프 대신 원).
// PlantStatus 키(= InverterStatus 상위집합)라 인버터·발전소 상태 양쪽을 그대로 색인할 수 있다.
const STATUS_DOT: Record<PlantStatus, string> = {
  normal: 'bg-heviton-success',
  warning: 'bg-heviton-warning',
  critical: 'bg-heviton-error',
  commLost: 'bg-heviton-none',
  preparing: 'bg-heviton-none',
};

/** 인버터 아이콘 타일 — 대시보드 지표 카드 계열과 통일한 size-9/rounded-lg 타일. 순수 장식(aria-hidden). */
export function InverterIcon({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        className,
      )}
    >
      <PlugZap className="size-4.5" />
    </span>
  );
}

/** 인버터 상태 점 — 이름 옆에 붙이는 컬러 원. 상태 미상(undefined)이면 렌더 안 함. */
export function InverterStatusDot({
  status,
  className,
}: {
  status?: PlantStatus;
  className?: string;
}) {
  if (!status) return null;
  return (
    <span
      aria-hidden="true"
      className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[status], className)}
    />
  );
}
